<template>
  <DataVisGraph
    :loading="$apollo.loading"
    :title="$t(title)"
    :local-date-range="DateRange"
    :local-active-filters="activeFilters"
    @remove-filter="removeFilter"
    @refetch="$apollo.queries.statisticsTable.refetch()"
    @updated-date-range="DateRange = $event"
  >
    <template #download-options-slot>
      <li>
        <ArrayToCsv v-slot="{ convertedString }" :entries="csvData">
          <a
            :href="convertedString"
            :download="`${$t(title)}-${DateRange.start}-${DateRange.end}.csv`"
          >
            CSV
          </a>
        </ArrayToCsv>
      </li>
    </template>
    <template #help-slot>
      {{ helpText }}
    </template>
    <!--  Little weird, but we don't have any options currently to show, so we leave this empty.
          The Span is there to prevent having a &nbsp; which fucks with the spacing of the icons.
      -->
    <template #dropdown><span style="display: none" /></template>
    <template #graph>
      <BaseDataTable
        ref="table"
        :loading="$apollo.loading"
        :columns="statisticsTable.columns ? statisticsTable.columns : []"
        :entries="statisticsTable.rows ? statisticsTable.rows : []"
        class="f-12"
      />
    </template>
  </DataVisGraph>
</template>

<script>
import gql from 'graphql-tag'
import DataVisGraph from '@/components/DataVisGraph.vue'
import BaseDataTable from '@/components/BaseDataTable'
import DateRange from '@/vue-prop-types/DateRange'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import ArrayToCsv from '@/components/utilities/ArrayToCsv.vue'
import { clean } from '@/utils/tools'

export default {
  components: {
    ArrayToCsv,
    DataVisGraph,
    BaseDataTable,
  },
  props: {
    /**
     * Title
     */
    title: {
      type: String,
      default: 'undefined title',
    },

    /**
     * The filter used by the backend to filter the data
     */
    filter: {
      type: Object,
      default: () => ({}), // new Object
    },

    /**
     *  This value is used for the backend to figure out where the data should be gotten from.
     */
    tableKey: {
      type: String,
      required: true,
    },

    /**
     * Text to display to help the user understand what is getting displayed
     */
    helpText: {
      type: String,
      default: null,
    },

    /**
     * The date range getting displayed in the header
     */
    dateRange: {
      type: DateRange,
      default: () => new DateRange(),
    },
  },
  data() {
    return {
      dateRangeLocal: null,
      statisticsTable: {},
    }
  },
  apollo: {
    statisticsTable: {
      query: gql`
        query statisticsTable(
          $tableKey: String
          $filter: String
          $dateFrom: Date
          $dateTo: Date
          $currency: Currency
        ) {
          statisticsTable(
            specifications: {
              tableKey: $tableKey
              filter: $filter
              currency: $currency
              dateRange: { from: $dateFrom, to: $dateTo }
            }
          ) {
            columns {
              key
              title
            }
            rows {
              data
            }
          }
        }
      `,
      variables() {
        return {
          ...this.filter,
          ...this.localFilter,
          ...{
            filter: JSON.stringify(this.filter.filter),
            tableKey: this.tableKey,
          },
        }
      },
      update: (data) => new TableDataHandler(data.statisticsTable),
    },
  },
  computed: {
    DateRange: {
      get() {
        return this.dateRangeLocal || this.dateRange
      },
      set(range) {
        this.dateRangeLocal = DateRange.copy(range)
      },
    },
    csvData() {
      const keys = this.statisticsTable.columns.map((c) => c.key)
      return this.statisticsTable.rows.map((row) => {
        const ob = {}
        keys.forEach((k) => {
          ob[k] = row[k]
        })
        return ob
      })
    },
    activeFilters() {
      const filter = clean({
        'Date Range': this.dateRangeLocal
          ? { id: 'dateRangeLocal', name: this.dateRangeLocal?.toString() }
          : null,
      })
      return Object.keys(filter).map((k) => ({
        type: k,
        value: filter[k].id || null,
        name: filter[k].name || null,
      }))
    },
    localFilter() {
      const FilterRange = this.dateRangeLocal
        ? DateRange.convertToFormat(this.dateRangeLocal, 'YYYY-MM-DD')
        : null
      return clean({
        dateFrom: FilterRange?.start || null,
        dateTo: FilterRange?.end || null,
      })
    },
  },
  methods: {
    removeFilter(filter) {
      if (filter.type === 'Date Range') {
        this.dateRangeLocal = null
      }
    },
  },
}
</script>

<style scoped></style>
